import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, Col, Empty, Row, Select, Space, Statistic, Table, Tabs,
  Tag, Typography, message,
} from 'antd';
import dayjs from 'dayjs';
import api from '../../api/axios';
import { getCompetitionYears, getCompetitions } from '../../api/competitions';
import apiErrorMessage from '../../utils/apiError';
import { numberSorter, pathTextSorter } from '../../utils/tableSorters';

const { Text } = Typography;

const SYMBOL = { TRY: '₺', EUR: '€', USD: '$' };
const money = (a, c) =>
  `${Number(a || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${SYMBOL[c] || c}`;

const STAGE_STATUS = {
  draft: 'Taslak', candidates: 'Aday Toplama', fee_approval: 'Ücret Onayı',
  confirmed: 'Kesinleşti', completed: 'Tamamlandı', cancelled: 'İptal',
};

const CompetitionsReportTab = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [years, setYears] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [filters, setFilters] = useState({ year: null, competition_id: null });

  const load = useCallback(async (override = filters) => {
    setLoading(true);
    try {
      const res = await api.get('/reports/competitions', {
        params: {
          year: override.year || undefined,
          competition_id: override.competition_id || undefined,
        },
      });
      setData(res.data);
    } catch (e) {
      message.error(apiErrorMessage(e, 'Yarışma raporu yüklenemedi.'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load({ year: null, competition_id: null });
    getCompetitionYears().then(({ data: y }) => setYears(y || [])).catch(() => setYears([]));
    getCompetitions({ per_page: 100 })
      .then(({ data: c }) => setCompetitions(c.data || []))
      .catch(() => setCompetitions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilter = (patch) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    load(next);
  };

  const goToStage = (stageId) => navigate('/competitions');

  // Liste tamami bellekte - istemci siralayicilari dogru calisir. Iki kolon
  // birden fazla alan gosterdigi icin siralama anlamli olan alana baglaniyor.
  const stageColumns = [
    {
      title: 'Yarışma',
      sorter: pathTextSorter((r) => r.competition),
      render: (_, r) => (
        <Space orientation="vertical" size={0}>
          <Text strong>{r.competition}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>{r.organization} · {r.year}</Text>
        </Space>
      ),
    },
    {
      title: 'Etap',
      sorter: pathTextSorter((r) => r.stage_name),
      render: (_, r) => (
        <Space orientation="vertical" size={0}>
          <Text>{r.stage_name} {r.is_abroad && <Tag color="blue">Yurt dışı</Tag>}</Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {r.start_date ? dayjs(r.start_date).format('DD.MM.YYYY') : '—'} · {STAGE_STATUS[r.status] || r.status}
          </Text>
        </Space>
      ),
    },
    { title: 'Toplam', dataIndex: 'total', width: 80, sorter: numberSorter('total') },
    { title: 'Aday', dataIndex: 'candidates', width: 70, sorter: numberSorter('candidates') },
    {
      title: 'Kesinleşen',
      dataIndex: 'confirmed',
      width: 95,
      sorter: numberSorter('confirmed'),
      render: (v) => <Tag color={v > 0 ? 'success' : 'default'}>{v}</Tag>,
    },
    {
      title: 'Vazgeçen',
      dataIndex: 'declined',
      width: 90,
      sorter: numberSorter('declined'),
      render: (v) => (v > 0 ? <Tag color="error">{v}</Tag> : '—'),
    },
    {
      title: 'Tahakkuk / Tahsilat',
      render: (_, r) => (
        r.money?.length ? (
          <Space orientation="vertical" size={0}>
            {r.money.map((m) => (
              <Text key={m.currency} style={{ fontSize: 12 }}>
                {money(m.accrued, m.currency)} → <Text type="success">{money(m.collected, m.currency)}</Text>
                {m.remaining > 0 && <Text type="warning"> (kalan {money(m.remaining, m.currency)})</Text>}
              </Text>
            ))}
          </Space>
        ) : <Text type="secondary">—</Text>
      ),
    },
  ];

  if (!data && loading) {
    return <Card loading />;
  }

  return (
    <div>
      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          placeholder="Yıl"
          allowClear
          style={{ width: 130 }}
          value={filters.year}
          onChange={(v) => applyFilter({ year: v, competition_id: null })}
          options={years.map((y) => ({ value: y, label: y }))}
        />
        <Select
          placeholder="Yarışma"
          allowClear
          showSearch
          optionFilterProp="label"
          style={{ width: 240 }}
          value={filters.competition_id}
          onChange={(v) => applyFilter({ competition_id: v })}
          options={competitions
            .filter((c) => !filters.year || c.year === filters.year)
            .map((c) => ({ value: c.id, label: `${c.name} (${c.year})` }))}
        />
        <Button onClick={() => navigate('/competitions')}>Yarışmalar Sayfası</Button>
      </Space>

      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small"><Statistic title="Etap" value={data?.summary?.stage_count ?? 0} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Onay Bekleyen Veli"
              value={data?.summary?.pending_count ?? 0}
              styles={{ content: { color: (data?.summary?.pending_count ?? 0) > 0 ? '#d46b08' : undefined } }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Gecikmiş Taksit"
              value={data?.summary?.overdue_count ?? 0}
              styles={{ content: { color: (data?.summary?.overdue_count ?? 0) > 0 ? '#cf1322' : undefined } }}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title="Eksik Pasaport"
              value={data?.summary?.missing_passports ?? 0}
              styles={{ content: { color: (data?.summary?.missing_passports ?? 0) > 0 ? '#cf1322' : undefined } }}
            />
          </Card>
        </Col>
      </Row>

      <Card size="small" title="Etap Özetleri" style={{ marginBottom: 16 }}>
        <Table
          rowKey="stage_id"
          size="small"
          loading={loading}
          dataSource={data?.stages || []}
          scroll={{ x: 900 }}
          pagination={{ pageSize: 15, showTotal: (t) => `${t} etap` }}
          locale={{ emptyText: <Empty description="Kayıtlı yarışma yok." /> }}
          columns={stageColumns}
          onRow={(r) => ({ onClick: () => goToStage(r.stage_id), style: { cursor: 'pointer' } })}
        />
      </Card>

      <Tabs
        items={[
          {
            key: 'pending',
            label: `Onay Bekleyenler (${data?.pending_consents?.length ?? 0})`,
            children: (
              <>
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Bu veliler bildirimi aldı ama henüz yanıtlamadı"
                  description="Taslak ve iptal edilmiş etaplar listede yok — onlar veliye zaten görünmüyor."
                />
                <Table
                  rowKey="participant_id"
                  size="small"
                  dataSource={data?.pending_consents || []}
                  pagination={{ pageSize: 20 }}
                  locale={{ emptyText: 'Bekleyen onay yok.' }}
                  columns={[
                    { title: 'Öğrenci', dataIndex: 'student' },
                    { title: 'Yarışma', dataIndex: 'competition' },
                    { title: 'Etap', dataIndex: 'stage' },
                    {
                      title: 'Bekleyen',
                      dataIndex: 'bekleyen',
                      width: 130,
                      render: (v) => (
                        <Tag color={v === 'katilim' ? 'processing' : 'warning'}>
                          {v === 'katilim' ? 'Katılım onayı' : 'Ücret onayı'}
                        </Tag>
                      ),
                    },
                  ]}
                />
              </>
            ),
          },
          {
            key: 'overdue',
            label: `Gecikmiş Taksitler (${data?.overdue?.length ?? 0})`,
            children: (
              <Table
                rowKey={(r) => `${r.participant_id}-${r.label}-${r.due_date}`}
                size="small"
                dataSource={data?.overdue || []}
                pagination={{ pageSize: 20 }}
                locale={{ emptyText: 'Gecikmiş taksit yok.' }}
                columns={[
                  { title: 'Öğrenci', dataIndex: 'student' },
                  { title: 'Yarışma', dataIndex: 'competition' },
                  { title: 'Etap', dataIndex: 'stage' },
                  { title: 'Taksit', dataIndex: 'label', width: 130 },
                  {
                    title: 'Vade',
                    dataIndex: 'due_date',
                    width: 120,
                    render: (v) => (v ? dayjs(v).format('DD.MM.YYYY') : '—'),
                  },
                  {
                    title: 'Gecikme',
                    dataIndex: 'gun',
                    width: 100,
                    render: (v) => <Tag color="error">{v} gün</Tag>,
                  },
                  {
                    title: 'Tutar',
                    dataIndex: 'amount',
                    width: 130,
                    render: (v, r) => <b>{money(v, r.currency)}</b>,
                  },
                ]}
              />
            ),
          },
          {
            key: 'passports',
            label: `Eksik Pasaport (${data?.missing_passports?.length ?? 0})`,
            children: (
              <>
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message="Yalnızca yurt dışı etaplarında"
                  description="Ücreti onaylamış ama katılacak kişilerin pasaport bilgisini tamamlamamış veliler."
                />
                <Table
                  rowKey="participant_id"
                  size="small"
                  dataSource={data?.missing_passports || []}
                  pagination={{ pageSize: 20 }}
                  locale={{ emptyText: 'Eksik pasaport bilgisi yok.' }}
                  columns={[
                    { title: 'Öğrenci', dataIndex: 'student' },
                    { title: 'Yarışma', dataIndex: 'competition' },
                    { title: 'Etap', dataIndex: 'stage' },
                    { title: 'Onaylanan Kişi', dataIndex: 'onaylanan', width: 130 },
                    { title: 'Girilen', dataIndex: 'girilen', width: 100 },
                    {
                      title: 'Eksik',
                      dataIndex: 'eksik',
                      width: 100,
                      render: (v) => <Tag color="error">{v}</Tag>,
                    },
                  ]}
                />
              </>
            ),
          },
        ]}
      />
    </div>
  );
};

export default CompetitionsReportTab;
